import { useEffect, useRef, useState } from 'react';
import { storage } from '@hypneuronerd/storage';
import type { Settings as SettingsType, Stats } from '../data/types';
import { DEFAULT_STATS } from '../data/types';
import styles from './Settings.module.css';

interface SettingsProps {
  settings: SettingsType;
  onChange: (next: SettingsType) => void;
}

export function Settings({ settings, onChange }: SettingsProps) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handle(ev: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }
    function escape(ev: KeyboardEvent) {
      if (ev.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', handle);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const stats = storage.get<Stats>('candle:stats', DEFAULT_STATS);

  return (
    <>
      <button
        type="button"
        className={styles.gear}
        onClick={() => setOpen((v) => !v)}
        aria-label="settings"
        aria-expanded={open}
      >
        ⚙
      </button>

      {open && (
        <div ref={panelRef} className={styles.panel} role="dialog" aria-label="settings">
          <h2 className={styles.heading}>settings</h2>

          <fieldset className={styles.field}>
            <legend className={styles.legendInline}>default duration</legend>
            <div className={styles.choices}>
              {([5, 10, 15, 20] as const).map((mins) => (
                <button
                  key={mins}
                  type="button"
                  className={`${styles.choice} ${
                    settings.defaultDuration === mins ? styles.choiceActive : ''
                  }`}
                  onClick={() => onChange({ ...settings, defaultDuration: mins })}
                >
                  {mins}m
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className={styles.field}>
            <legend className={styles.legendInline}>sound when finished</legend>
            <div className={styles.choices}>
              {(['none', 'chime', 'gong'] as const).map((sound) => (
                <button
                  key={sound}
                  type="button"
                  className={`${styles.choice} ${
                    settings.endSound === sound ? styles.choiceActive : ''
                  }`}
                  onClick={() => onChange({ ...settings, endSound: sound })}
                >
                  {sound}
                </button>
              ))}
            </div>
          </fieldset>

          <div className={styles.stats}>
            <span>
              {stats.totalSessions} candle{stats.totalSessions === 1 ? '' : 's'} lit
            </span>
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                if (confirm('clear your candle history?')) {
                  storage.remove('candle:stats');
                  if (typeof window !== 'undefined') window.location.reload();
                }
              }}
            >
              clear history
            </button>
          </div>
        </div>
      )}
    </>
  );
}
